import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../Config/config";
import { useTenant } from "../Context/TenantContext";
import CollegeHeader from "./CollegeHeader";
import CollegeHomeSideBar from "./CollegeHomeSideBar";
import CollegeFooter from "./CollegeFooter";
import { useNavigate } from "react-router-dom";
import {
  Building2, Mail, Phone, Globe, Camera, Trash2,
  Eye, EyeOff, Lock, ImagePlus, CheckCircle2,
  AlertCircle, ChevronLeft, Save,
} from "lucide-react";
import { parseLogoUrl } from "../utils/logoUtils";
import { resolveImageUrl } from "../utils/imageUtils";

// --- Design Tokens ------------------------------------------------------------
const T = {
  border:      "1px solid #E2E8F0",
  radius:      { sm: "8px", md: "12px", lg: "16px", xl: "20px", xxl: "24px", full: "999px" },
  shadow:      { xs: "0 1px 3px rgba(0,0,0,0.05)", sm: "0 2px 8px rgba(0,0,0,0.07)", md: "0 4px 20px rgba(0,0,0,0.08)" },
  brand:       "var(--primary-blue, #4F46E5)",
  brandLight:  "rgba(79, 70, 229,0.08)",
  brandBorder: "rgba(79, 70, 229,0.18)",
  text: {
    heading:  "#0F172A",
    body:     "#334155",
    muted:    "#64748B",
    disabled: "#94A3B8",
  },
  bg: { main: "#F8FAFC", section: "#FFFFFF" },
};

// --- Section card wrapper ------------------------------------------------------
const Section = ({ title, icon: Icon, children, accent }) => (
  <div style={{
    background: T.bg.section, border: T.border, borderRadius: T.radius.xl,
    boxShadow: T.shadow.xs, overflow: "hidden", marginBottom: "20px",
  }}>
    {accent && <div style={{ height: "3px", background: "linear-gradient(90deg, var(--primary-blue, #4F46E5), #7C3AED)" }} />}
    <div style={{ padding: "24px 28px" }}>
      {(title || Icon) && (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
          {Icon && <Icon style={{ width: "15px", height: "15px", color: T.brand }} />}
          <p style={{
            fontSize: "11px", fontWeight: 700, color: T.text.muted,
            textTransform: "uppercase", letterSpacing: "0.06em", margin: 0,
          }}>
            {title}
          </p>
        </div>
      )}
      {children}
    </div>
  </div>
);

// --- Form field ---------------------------------------------------------------
const Field = ({ label, id, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
    <label
      htmlFor={id}
      style={{ fontSize: "11px", fontWeight: 700, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}
    >
      {label}
    </label>
    {children}
  </div>
);

const inputBase = {
  width: "100%", padding: "11px 14px", fontSize: "14px", fontWeight: 600,
  color: "#0F172A", background: "#F8FAFC", border: "1.5px solid #E2E8F0",
  borderRadius: "12px", outline: "none", transition: "border-color 0.15s, box-shadow 0.15s",
  boxSizing: "border-box",
};

const TextInput = ({ id, value, onChange, placeholder, type = "text", disabled, onFocus, onBlur, focused }) => (
  <input
    id={id}
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    disabled={disabled}
    onFocus={onFocus}
    onBlur={onBlur}
    style={{
      ...inputBase,
      borderColor: focused ? T.brand : "#E2E8F0",
      boxShadow:   focused ? `0 0 0 3px ${T.brandLight}` : "none",
      color:       disabled ? T.text.muted : T.text.heading,
      cursor:      disabled ? "not-allowed" : "text",
    }}
  />
);

// --- Password input with show/hide toggle -------------------------------------
const PasswordInput = ({ id, value, onChange, placeholder, focused, onFocus, onBlur }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input
        id={id}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        onFocus={onFocus}
        onBlur={onBlur}
        style={{
          ...inputBase,
          paddingRight: "44px",
          borderColor: focused ? T.brand : "#E2E8F0",
          boxShadow:   focused ? `0 0 0 3px ${T.brandLight}` : "none",
        }}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        style={{
          position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)",
          background: "none", border: "none", cursor: "pointer", padding: "4px",
          color: T.text.muted, display: "flex", alignItems: "center",
        }}
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show
          ? <EyeOff style={{ width: "16px", height: "16px" }} />
          : <Eye    style={{ width: "16px", height: "16px" }} />}
      </button>
    </div>
  );
};

// --- EditCollegeProfile -------------------------------------------------------
const EditCollegeProfile = () => {
  const navigate = useNavigate();
  const { getLogoUrl } = useTenant();
  const logoInputRef   = useRef(null);
  const imagesInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "", state: "", country: "",
    email: "", phone: "", website: "", username: "",
    oldPassword: "", newPassword: "", confirmPassword: "",
  });

  const [logoPreview,    setLogoPreview]    = useState(null);
  const [logoFile,       setLogoFile]       = useState(null);
  const [newImages,      setNewImages]      = useState([]);       // File objects queued
  const [existingImages, setExistingImages] = useState([]);       // Normalised URL strings
  const [status,         setStatus]         = useState(null);     // { type: "ok"|"err", msg }
  const [saving,         setSaving]         = useState(false);
  const [focused,        setFocused]        = useState(null);

  // -- Fetch current data -------------------------------------------------------
  const fetchCollegeData = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user?.username) return;
    try {
      const res  = await axios.get(`${API_BASE_URL}/colleges/username/${user.username}`);
      const data = res.data;

      setFormData({
        name:    data.name    || "",
        state:   data.state   || "",
        country: data.country || "",
        email:   data.email   || "",
        phone:   data.phone   || "",
        website: data.website || "",
        username: data.username || "",
        oldPassword: "", newPassword: "", confirmPassword: "",
      });

      if (data.logo) {
        const logoUrl = parseLogoUrl(data.logo);
        setLogoPreview(logoUrl);
      } else {
        setLogoPreview(null);
      }

      if (data.images) {
        const parseImgs = (raw) => {
          if (!raw) return [];
          if (Array.isArray(raw)) return raw.filter(u => u && typeof u === 'string');
          const s = String(raw).trim();
          if (s.startsWith('{') && s.endsWith('}')) {
            return s.slice(1, -1).split(',').map(u => u.trim().replace(/^"|"$/g, '')).filter(Boolean);
          }
          try { const p = JSON.parse(s); return Array.isArray(p) ? p.filter(u => u && typeof u === 'string') : []; } catch {}
          return [];
        };
        const imgs = parseImgs(data.images);
        setExistingImages(imgs.filter(img => img && typeof img === "string"));
      }
    } catch (err) {
      console.error("Failed to fetch college data:", err);
      setStatus({ type: "err", msg: "Failed to load profile data." });
    }
  };

  useEffect(() => { fetchCollegeData(); }, []);

  // -- Handlers -----------------------------------------------------------------
  const handleChange = (field) => (e) => {
    if (field === "phone") {
      const cleaned = e.target.value.replace(/\D/g, "");
      if (cleaned.length <= 10) setFormData((p) => ({ ...p, phone: cleaned }));
    } else {
      setFormData((p) => ({ ...p, [field]: e.target.value }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setNewImages((p) => [...p, ...files]);
  };

  const removeNewImage = (idx) => setNewImages((p) => p.filter((_, i) => i !== idx));
  const removeExistingImage = (idx) => setExistingImages((p) => p.filter((_, i) => i !== idx));

  // -- Submit --------------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    if (formData.phone && formData.phone.length !== 10)
      return setStatus({ type: "err", msg: "Phone number must be exactly 10 digits." });
    if (formData.newPassword && formData.newPassword !== formData.confirmPassword)
      return setStatus({ type: "err", msg: "New passwords do not match." });

    setSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const fd   = new FormData();
      Object.entries(formData).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (logoFile) fd.append("logo", logoFile);
      newImages.forEach((img) => { if (img instanceof File) fd.append("images", img); });

      // existingImages are already absolute S3/CloudFront URLs — send them as-is
      existingImages.forEach((img) => {
        if (img && img.trim()) fd.append("existingImages", img);
      });

      await axios.put(`${API_BASE_URL}/colleges/update-profile/${user.username}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setStatus({ type: "ok", msg: "Profile updated successfully!" });
      setNewImages([]);
      const blobUrl = logoFile ? logoPreview : null;
      await fetchCollegeData();
      // If we uploaded a logo but the API returned an unparseable URL, keep the blob preview
      if (blobUrl) setLogoPreview(prev => prev || blobUrl);
    } catch (err) {
      console.error("Error updating profile:", err);
      setStatus({ type: "err", msg: "Failed to update profile. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="college-main-layout" style={{ background: T.bg.main, fontFamily: "inherit" }}>
      <CollegeHeader />

      <div className="college-content-wrapper">
        <CollegeHomeSideBar />

        <main className="college-main-content">
          <form onSubmit={handleSubmit}>
            <div style={{ maxWidth: "860px", margin: "0 auto", padding: "36px 28px 72px" }}>

              {/* -- Page Header ----------------------------------------------- */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: "16px", marginBottom: "32px", flexWrap: "wrap",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  <div style={{
                    width: "48px", height: "48px", borderRadius: T.radius.lg,
                    background: T.brandLight, border: `1px solid ${T.brandBorder}`,
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <Building2 style={{ width: "22px", height: "22px", color: T.brand }} />
                  </div>
                  <div>
                    <h1 style={{ fontSize: "22px", fontWeight: 800, margin: 0, color: T.text.heading, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
                      Edit Profile
                    </h1>
                    <p style={{ fontSize: "13px", color: T.text.muted, margin: "4px 0 0", fontWeight: 500 }}>
                      Update your institution's information
                    </p>
                  </div>
                </div>

                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => navigate("/CollegeViewProfile")}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: "6px",
                      padding: "9px 18px", borderRadius: T.radius.md,
                      background: T.bg.section, color: T.text.body,
                      border: T.border, cursor: "pointer",
                      fontSize: "13px", fontWeight: 600,
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = T.bg.main; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = T.bg.section; }}
                  >
                    <ChevronLeft style={{ width: "14px", height: "14px" }} />
                    View Profile
                  </button>

                  <button
                    type="submit"
                    disabled={saving}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: "7px",
                      padding: "9px 22px", borderRadius: T.radius.md,
                      background: saving ? "#93C5FD" : T.brand,
                      color: "#FFFFFF", border: "none", cursor: saving ? "not-allowed" : "pointer",
                      fontSize: "13px", fontWeight: 700,
                      boxShadow: saving ? "none" : "0 3px 12px rgba(79, 70, 229,0.28)",
                      transition: "opacity 0.15s",
                    }}
                    onMouseEnter={(e) => { if (!saving) e.currentTarget.style.opacity = "0.88"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                  >
                    <Save style={{ width: "14px", height: "14px" }} />
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>

              {/* -- Status banner --------------------------------------------- */}
              {status && (
                <div style={{
                  display: "flex", alignItems: "flex-start", gap: "10px",
                  padding: "14px 18px", borderRadius: T.radius.lg, marginBottom: "20px",
                  background: status.type === "ok" ? "#F0FDF4" : "#FEF2F2",
                  border: `1px solid ${status.type === "ok" ? "#BBF7D0" : "#FECACA"}`,
                }}>
                  {status.type === "ok"
                    ? <CheckCircle2 style={{ width: "16px", height: "16px", color: "#16A34A", flexShrink: 0, marginTop: "1px" }} />
                    : <AlertCircle  style={{ width: "16px", height: "16px", color: "#DC2626", flexShrink: 0, marginTop: "1px" }} />}
                  <p style={{
                    fontSize: "13px", fontWeight: 600, margin: 0,
                    color: status.type === "ok" ? "#16A34A" : "#DC2626",
                  }}>
                    {status.msg}
                  </p>
                </div>
              )}

              {/* -- Logo & Identity ------------------------------------------- */}
              <Section accent>
                <div style={{ display: "flex", alignItems: "center", gap: "28px", flexWrap: "wrap" }}>
                  {/* Logo picker */}
                  <div style={{ position: "relative", flexShrink: 0 }}>
                    <div style={{
                      width: "96px", height: "96px", borderRadius: T.radius.xl,
                      background: T.bg.main, border: `2px solid ${T.brandBorder}`,
                      overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
                      boxShadow: T.shadow.sm,
                    }}>
                      {logoPreview
                        ? <img
                            src={logoPreview}
                            alt="College Logo"
                            style={{ width: "100%", height: "100%", objectFit: "contain", padding: "8px" }}
                          />
                        : <Camera style={{ width: "28px", height: "28px", color: T.text.muted }} />
                      }
                    </div>
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      style={{
                        position: "absolute", bottom: "-6px", right: "-6px",
                        width: "30px", height: "30px", borderRadius: T.radius.full,
                        background: T.brand, border: "2px solid #fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: "pointer", boxShadow: T.shadow.sm,
                      }}
                      aria-label="Change logo"
                    >
                      <Camera style={{ width: "13px", height: "13px", color: "#fff" }} />
                    </button>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      style={{ display: "none" }}
                    />
                  </div>

                  {/* Identity fields */}
                  <div style={{ flex: 1, minWidth: "220px", display: "flex", flexDirection: "column", gap: "16px" }}>
                    <Field label="Institution Name" id="name">
                      <TextInput
                        id="name" value={formData.name} onChange={handleChange("name")}
                        placeholder="Full institution name"
                        focused={focused === "name"}
                        onFocus={() => setFocused("name")} onBlur={() => setFocused(null)}
                      />
                    </Field>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                      <Field label="State" id="state">
                        <TextInput
                          id="state" value={formData.state} onChange={handleChange("state")}
                          placeholder="State / Province"
                          focused={focused === "state"}
                          onFocus={() => setFocused("state")} onBlur={() => setFocused(null)}
                        />
                      </Field>
                      <Field label="Country" id="country">
                        <TextInput
                          id="country" value={formData.country} onChange={handleChange("country")}
                          placeholder="Country"
                          focused={focused === "country"}
                          onFocus={() => setFocused("country")} onBlur={() => setFocused(null)}
                        />
                      </Field>
                    </div>
                  </div>
                </div>
              </Section>

              {/* -- Contact Information --------------------------------------- */}
              <Section title="Contact Information" icon={Mail}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "16px" }}>
                  <Field label="Email Address" id="email">
                    <TextInput
                      id="email" type="email" value={formData.email} onChange={handleChange("email")}
                      placeholder="admin@college.edu"
                      focused={focused === "email"}
                      onFocus={() => setFocused("email")} onBlur={() => setFocused(null)}
                    />
                  </Field>
                  <Field label="Phone Number" id="phone">
                    <TextInput
                      id="phone" value={formData.phone} onChange={handleChange("phone")}
                      placeholder="10-digit number"
                      focused={focused === "phone"}
                      onFocus={() => setFocused("phone")} onBlur={() => setFocused(null)}
                    />
                  </Field>
                  <Field label="Website URL" id="website">
                    <TextInput
                      id="website" type="url" value={formData.website} onChange={handleChange("website")}
                      placeholder="https://yourcollegeweb.edu"
                      focused={focused === "website"}
                      onFocus={() => setFocused("website")} onBlur={() => setFocused(null)}
                    />
                  </Field>
                  <Field label="Admin Username" id="username">
                    <TextInput
                      id="username" value={formData.username} disabled
                      placeholder="-"
                      focused={false}
                    />
                  </Field>
                </div>
              </Section>

              {/* -- Security -------------------------------------------------- */}
              <Section title="Security Settings" icon={Lock}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "16px" }}>
                  <Field label="Current Password" id="oldPassword">
                    <PasswordInput
                      id="oldPassword" value={formData.oldPassword} onChange={handleChange("oldPassword")}
                      placeholder="Current password"
                      focused={focused === "oldPassword"}
                      onFocus={() => setFocused("oldPassword")} onBlur={() => setFocused(null)}
                    />
                  </Field>
                  <Field label="New Password" id="newPassword">
                    <PasswordInput
                      id="newPassword" value={formData.newPassword} onChange={handleChange("newPassword")}
                      placeholder="New password"
                      focused={focused === "newPassword"}
                      onFocus={() => setFocused("newPassword")} onBlur={() => setFocused(null)}
                    />
                  </Field>
                  <Field label="Confirm Password" id="confirmPassword">
                    <PasswordInput
                      id="confirmPassword" value={formData.confirmPassword} onChange={handleChange("confirmPassword")}
                      placeholder="Confirm new password"
                      focused={focused === "confirmPassword"}
                      onFocus={() => setFocused("confirmPassword")} onBlur={() => setFocused(null)}
                    />
                  </Field>
                </div>
                <p style={{ fontSize: "12px", color: T.text.disabled, margin: "12px 0 0", fontWeight: 500 }}>
                  Leave password fields blank to keep your current password.
                </p>
              </Section>

              {/* -- Campus Images ---------------------------------------------- */}
              <Section title="Campus Showcase" icon={ImagePlus}>
                {/* Upload trigger */}
                <button
                  type="button"
                  onClick={() => imagesInputRef.current?.click()}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "8px",
                    padding: "9px 18px", borderRadius: T.radius.md,
                    background: T.brandLight, border: `1px solid ${T.brandBorder}`,
                    color: T.brand, cursor: "pointer", fontSize: "13px", fontWeight: 700,
                    marginBottom: "18px", transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(79, 70, 229,0.13)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = T.brandLight; }}
                >
                  <ImagePlus style={{ width: "14px", height: "14px" }} />
                  Add Campus Photos
                </button>
                <input
                  ref={imagesInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImagesChange}
                  style={{ display: "none" }}
                />

                {/* Queued new images */}
                {newImages.length > 0 && (
                  <div style={{ marginBottom: "16px" }}>
                    <p style={{
                      fontSize: "10px", fontWeight: 700, color: T.brand,
                      textTransform: "uppercase", letterSpacing: "0.06em",
                      margin: "0 0 10px",
                    }}>
                      {newImages.length} photo{newImages.length !== 1 ? "s" : ""} queued for upload
                    </p>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: "10px" }}>
                      {newImages.map((file, idx) => (
                        <div key={idx} style={{ position: "relative", aspectRatio: "16/10", borderRadius: T.radius.lg, overflow: "hidden", border: T.border }}>
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`New ${idx + 1}`}
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                          />
                          <button
                            type="button"
                            onClick={() => removeNewImage(idx)}
                            style={{
                              position: "absolute", top: "4px", right: "4px",
                              width: "22px", height: "22px", borderRadius: T.radius.full,
                              background: "#EF4444", border: "none", cursor: "pointer",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              boxShadow: T.shadow.sm,
                            }}
                            aria-label="Remove image"
                          >
                            <Trash2 style={{ width: "11px", height: "11px", color: "#fff" }} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Existing images */}
                {existingImages.length > 0 ? (
                  <div>
                    <p style={{
                      fontSize: "10px", fontWeight: 700, color: T.text.muted,
                      textTransform: "uppercase", letterSpacing: "0.06em",
                      margin: "0 0 10px",
                    }}>
                      Saved photos - {existingImages.length} photo{existingImages.length !== 1 ? "s" : ""}
                    </p>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "10px" }}>
                      {existingImages.map((img, idx) => (
                        <div key={idx} style={{ position: "relative", aspectRatio: "16/10", borderRadius: T.radius.lg, overflow: "hidden", border: T.border, boxShadow: T.shadow.xs }}>
                          <img
                            src={resolveImageUrl(img)}
                            alt={`Campus ${idx + 1}`}
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/logo192.png";
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => removeExistingImage(idx)}
                            style={{
                              position: "absolute", top: "4px", right: "4px",
                              width: "22px", height: "22px", borderRadius: T.radius.full,
                              background: "#EF4444", border: "none", cursor: "pointer",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              boxShadow: T.shadow.sm,
                            }}
                            aria-label="Remove image"
                          >
                            <Trash2 style={{ width: "11px", height: "11px", color: "#fff" }} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : newImages.length === 0 ? (
                  <div style={{
                    padding: "36px 24px", textAlign: "center",
                    background: T.bg.main, borderRadius: T.radius.lg, border: T.border,
                  }}>
                    <span style={{ fontSize: "26px", display: "block", marginBottom: "8px", opacity: 0.4 }}>🏛️</span>
                    <p style={{ fontSize: "13px", color: T.text.muted, margin: "0 0 4px", fontWeight: 600 }}>No campus images yet</p>
                    <p style={{ fontSize: "12px", color: T.text.disabled, margin: 0 }}>Add photos to showcase your campus.</p>
                  </div>
                ) : null}
              </Section>

            </div>
          </form>
        </main>
      </div>

      <CollegeFooter />

      <style>{`
        footer[aria-label*="college Portal footer"] {
           transition: margin-left 300ms ease;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default EditCollegeProfile;